import { Injectable, OnDestroy } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { collection, doc, DocumentData, Firestore, getDoc, getDocs, onSnapshot, QuerySnapshot } from '@angular/fire/firestore';
import { Data } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { AuthStateService } from '@core/auth-state.service';
import { CatchErrorService } from '@core/catch-error.service';
import { LoadingService } from '@core/loading.service';
import { Auth, getAuth } from 'firebase/auth';
import { Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import moment from 'moment-timezone';
import { catchError, defer, delay, finalize, from, map, Observable, of, retryWhen, scan, shareReplay, Subject, Subscription, switchMap, take, takeUntil, throwError, timer } from 'rxjs';
import { GamesFeedSampleService } from 'src/app/pages/games-feed-sample/games-feed-sample.service';
import { RequestProfileService } from 'src/app/services/RequestBodyService/RequestProfile/request-profile.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthGamesService implements OnDestroy {
  private subscriptions = new Subscription();
  private gamefeedUnsubscribeInbox$: Subject<void> = new Subject<void>();

  timerSubscription: Subscription;
  private destroy$ = new Subject<void>();
  /** Prevents duplicate assigntimebasedchallengesv2 when game-new + panel both detect expiry. */
  private timeBasedAssignInFlight = false;
  private lastTimeBasedAssignAtMs = 0;
  private static readonly TIME_BASED_ASSIGN_DEDUPE_MS = 15_000;
  constructor(
    private firestore: Firestore,
    private catchService: CatchErrorService,
    private requestProfileService: RequestProfileService,
    private loadingService: LoadingService,
    public angularFireFunctions: AngularFireFunctions,
    public gamesFeedService: GamesFeedSampleService,
    public authService: AuthService,
    private authStateService: AuthStateService

  ) {
    this.updatePromotionalGames();
   /*  this.subscriptions.add(this.gamesFeedService.updateGameData$.subscribe());
    this.subscriptions.add(this.gamesFeedService.updateRapidFireGameData$.subscribe());
    this.subscriptions.add(this.gamesFeedService.updateInstantWinGameData$.subscribe());
    this.subscriptions.add(this.gamesFeedService.updateSkillGameData$.subscribe());
    this.subscriptions.add(this.gamesFeedService.updateGameOrderData$.subscribe());
    this.subscriptions.add(this.gamesFeedService.updateGameRecommendationData$.subscribe());
    this.subscriptions.add(this.gamesFeedService.updateGameBannerData$.subscribe());
    this.subscriptions.add(this.gamesFeedService.updateGameRecommendationRetoolData$.subscribe());
    this.subscriptions.add(this.gamesFeedService.updateGameBannerRetoolData$.subscribe()); */
    this.scheduleDailyApiCall();
  }

  getGameData$() { return this.gamesFeedService.updateGameData$; }
getRapidFireGameData$() { return this.gamesFeedService.updateRapidFireGameData$; }
getInstantWinGameData$() { return this.gamesFeedService.updateInstantWinGameData$; }
getSkillGameData$() { return this.gamesFeedService.updateSkillGameData$; }
getGameOrderData$() { return this.gamesFeedService.updateGameOrderData$; }
getGameRecommendationData$() { return this.gamesFeedService.updateGameRecommendationData$; }
getGameBannerData$() { return this.gamesFeedService.updateGameBannerData$; }
getGameRecommendationRetoolData$() { return this.gamesFeedService.updateGameRecommendationRetoolData$; }
getGameBannerRetoolData$() { return this.gamesFeedService.updateGameBannerRetoolData$; }


  // Function to schedule the updategamepromotionv2 CF call at 12:00 AM UK time
  scheduleDailyApiCall() {
    return this.authService.getUid$("AuthGamesService").pipe(
      switchMap((userId) => {
        // Get the current time
        const now = moment();
        // Set the target time to 12:00 AM UK time
        let uktime = moment.tz('00:00', 'HH:mm', 'Europe/London');

        // If it's already past 12:00 AM, schedule for the next day
        if (now.isSameOrAfter(uktime)) {
          uktime.add(1, 'day');
        }

        // Calculate the delay until the first call
        const firstCallDelay = uktime.diff(now);

        const msInADay = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

        // Use RxJS timer to handle scheduling
        return timer(firstCallDelay, msInADay).pipe(
          takeUntil(this.destroy$),
          switchMap(() => {
            const req = {
              userId,
              page: "game",
              method: "createGames",
            };
            // Return the actual HTTP observable instead of subscribing
            return this.authService.updateMainPages(req);
          }),
          catchError((error) => {
            console.error("Error in scheduled API call:", error);
            return of(null); // Gracefully handle error
          })
        );
      })
    );
  }

  getUserNewChallenges() {
     return this.authService.getUid$("AuthGamesService").pipe(
      switchMap((userId) => {
        if (!userId) {
          console.error("Invalid User ID.");
          return of(null);
        }

        const path_id = "5LJkHBPx39FKwe947ltI";
        const method = "read";

        return new Promise((resolve, reject) => {
          this.authService
            ?.getV2CloudFunction("readfirestoredoc", { path_id, method })
            .pipe(
              map((snapshot: Data) => snapshot?.data) // extract data
            )
            .subscribe({
              next: (data) => {
                resolve(data);
              },
              error: (error) => {
                console.error("Error fetching user new challenges data:", error);
                reject(error);
              }
            });
        });
      })
    );
  }


  //This method is used to get prize box data
  getPrizeBoxGameData$ = defer(() => {
    return from(getDocs(collection(this.firestore, `general_modules/game/${environment.mirrorPath}prize_box`))).pipe(
      map((snapshot: QuerySnapshot<DocumentData>) =>
        snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
      ),
      shareReplay(1),
      catchError((error) => {
        console.error("Error fetching prize box game data:", error);
        return throwError(() => error); // Pass the error to the Observable chain
      })
    );
  });

  onTimeBasedChallengeExpiry() {
    const now = Date.now();
    if (
      this.timeBasedAssignInFlight ||
      now - this.lastTimeBasedAssignAtMs < AuthGamesService.TIME_BASED_ASSIGN_DEDUPE_MS
    ) {
      return;
    }

    this.timeBasedAssignInFlight = true;
    this.lastTimeBasedAssignAtMs = now;
    this.getV2CloudFunction("assigntimebasedchallengesv2", {})
      .pipe(finalize(() => {
        this.timeBasedAssignInFlight = false;
      }))
      .subscribe();
  }

  onWeeklyChallengeExpiry() {
    this.getV2CloudFunction("assignweeklychallengesv2", {}).subscribe();
  }

  //Call updateGamePromotion to update the games in promotional slider
  async updatePromotionalGames(): Promise<void> {
    try {
      const auth: Auth = getAuth();
      if (!auth?.currentUser?.uid) return;

      const userId = auth.currentUser.uid;

      // Fetch user data via Cloud Function to respect Firestore security rules
      const path_id = "yzF1Ss5eiMqnh91zQMtv"; // pwa_cru_path doc for users/{userId}
      const method = "read";

      const userDataResult = await this.authService.getV2CloudFunction("readfirestoredoc", {
        path_id,
        method,
      }).toPromise();

      if (!userDataResult?.success || !userDataResult?.data) {
        console.warn("Unable to fetch user data for promo game update");
        return;
      }

      const user = userDataResult.data as { last_promo_game_dateTime?: Timestamp };
      const now = new Date();

      if (user?.last_promo_game_dateTime) {
        const lastPromoGameDate = typeof user.last_promo_game_dateTime === 'string' 
          ? new Date(user.last_promo_game_dateTime)
          : user.last_promo_game_dateTime instanceof Timestamp 
          ? user.last_promo_game_dateTime.toDate()
          : new Date(user.last_promo_game_dateTime);
        
        const diff = this.diffMinutes(lastPromoGameDate, now);

        if (diff >= 30) {
          // Perform housekeeping and update promotion
          this.pwaHouseKepping(userId);
          this.updateGamePromotion(userId);

          const update_path_id = "yzF1Ss5eiMqnh91zQMtv";
          const doc_data = { last_promo_game_dateTime: new Date() };
          const update_method = "update";

          await this.authService.getV2CloudFunction("updatefirestoredoc", {
            path_id: update_path_id,
            doc_data,
            method: update_method,
          }).toPromise();
        }
      } else {
        // First-time set
        const update_path_id = "yzF1Ss5eiMqnh91zQMtv";
        const doc_data = { last_promo_game_dateTime: new Date() };
        const update_method = "update";

        await this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id: update_path_id,
          doc_data,
          method: update_method,
        }).toPromise();
      }
    } catch (err) {
      console.error("Error in updatePromotionalGames:", err);
    }
  }
  /**
   * This function is called when a user clicks a button on the front end, and it calls a function in
   * the cloud functions that updates the user's game promotion status.
   * @param {string} userId - string
   */
  updateGamePromotion(userId: string) {
    this.getV2CloudFunction("updategamepromotionv2", userId).subscribe();
    // const updateGamePromotion = this.angularFireFunctions.httpsCallable("updateGamePromotion");
    // updateGamePromotion(userId)?.subscribe();
  }

  //This function is used to call housekeeping CF
  pwaHouseKepping(userId: string) {
    // V1
    /* const houseKeeping = this.angularFireFunctions.httpsCallable("houseKeeping");
    houseKeeping(userId) */
    //V2
    this.getV2CloudFunction("housekeepingv2", userId)?.subscribe(() => {
      localStorage.setItem("pwaHouseKeeping", "true");
    });
    /* const syndicateHouseKepping = this.angularFireFunctions.httpsCallable("houseKeppingUserSyndicate");
    syndicateHouseKepping(userId).subscribe();
    const houseKeppingGameSessionEnd = this.angularFireFunctions.httpsCallable("houseKeppingGameSessionEnd");
    houseKeppingGameSessionEnd(userId).subscribe();
    const houseKeppingVoucher = this.angularFireFunctions.httpsCallable("houseKeppingVoucher");
    houseKeppingVoucher(userId).subscribe();
    const houseKeppingHomeCards = this.angularFireFunctions.httpsCallable("houseKeppingHomeCards");
    houseKeppingHomeCards(userId).subscribe();
    const houseKeppingInboxDm = this.angularFireFunctions.httpsCallable("houseKeppingInboxDm");
    houseKeppingInboxDm(userId).subscribe(); */
  }

  public getV2CloudFunction(CloudFunctionName: string, payload: Data | string, options: Data = {}) {
    try {
      const functions = getFunctions();
      functions.region = AuthService.FIREBASE_REGION;
      return defer(() => {
        return from(httpsCallable(functions, CloudFunctionName, options)(payload));
      }).pipe(
        map((res: Data) => {
          return res?.data;
        }),
        retryWhen((errors) =>
          errors.pipe(
            scan((retryCount, error) => {
              if (retryCount >= 3) {
                throw error;
              }
              return retryCount + 1;
            }, 0),
            delay(1000) // Delay for retry in milliseconds
          )
        ),
        catchError((error) => {
          return throwError(new Error(`Error in ${CloudFunctionName}, Reason: ${error}`));
        })
      );
    } catch (error) {
      return throwError(error);
    }
  }

  // Get data from Angular firestore
  getAngularFireStore(url: string, method: string) {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) return of(null); // Return null as an observable if offline

        const docRef = doc(this.firestore, url); // Use Firestore's modular API to reference the document
        const needsAuthGate = typeof url === 'string' && url.includes('users/');

        const runOp = () => {
          if (method === 'get') {
            // Handle "get" method with getDoc and convert the Promise to an Observable
            return from(getDoc(docRef)).pipe(
              this.catchService.retryWhen,
              this.catchService.catchOfflineError
            );
          }

          if (method === 'snapshotChanges') {
            // Handle "snapshotChanges" method with Firestore's onSnapshot
            return new Observable((subscriber) => {
              const unsubscribe = onSnapshot(
                docRef,
                (snapshot) => {
                  subscriber.next(snapshot.data() as DocumentData);
                },
                (error) => {
                  subscriber.error(error);
                }
              );
              return () => unsubscribe(); // Cleanup subscription
            }).pipe(
              this.catchService.retryWhen,
              this.catchService.catchOfflineError
            );
          }

          return of(null); // Return null if method is unsupported
        };

        // Gate user-private paths only (LOTTOSOCIAL-PWA-1MEE); public paths unchanged
        return needsAuthGate
          ? this.authStateService.customTokenReady$.pipe(take(1), switchMap(() => runOp()))
          : runOp();
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1) // Cache the response
    );
  }
  // Check minutes difference between 2 dates
  private diffMinutes(dt1: Date, dt2: Date) {
    let diff = (dt2.getTime() - dt1.getTime()) / 1000;
    diff /= 60;
    return Math.abs(Math.round(diff));
  }

  // this code is for updating User point
  updateCurrentBalanceGame(updateBalance: number): Observable<any> {
    return this.authService.getUid$("AuthGamesService").pipe(
      switchMap((userId) => {
        if (!userId) {
          console.error("Invalid User ID.");
          return of(null);
        }

        const path_id = "SK8mBmn1R6bNDdj1cul8";
        const doc_data = { points: updateBalance };
        const method = "update";

        // return the observable instead of subscribing inside
        return this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).pipe(
          catchError((err) => {
            console.error("Error updating current balance:", err);
            return of(null);
          })
        );
      })
    );
  }


  feedBackTypeForm(
    surveyLink: string,
    hiddenData: Data,
    typeFormId: string,
    pointValue?: number
  ): void {
    const type = hiddenData?.type;
    this.loadingService.present();


    window["typeformEmbed"]
      ?.makePopup(surveyLink, {
        hideHeaders: true,
        hideFooter: true,
        hidden: [hiddenData],
        onSubmit: () => {
          this.authService.getUid$("AuthGamesService").pipe(
            switchMap((userId) => {
              if (!userId) {
                console.error("Invalid User ID.");
                return of(null);
              }
              return of(userId);
            })
          );
          const path_id = "pGxBYtRO4mdgNkboYdSy";
          const doc_data = { viewed: true };
          const dv = { docId: typeFormId };
          const method = "update";

          this.authService.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            dv,
            method,
          }).subscribe({
            error: (err) => {
              console.error('Error updating notification status:', err);
            }
          });
          if (type === "Points") {
            const updateBalance = hiddenData?.Point_balance + (pointValue || 0);
            this.updateCurrentBalanceGame(updateBalance).subscribe({
              error: (err) => console.error("Error updating balance:", err),
            });
          }
          if (type === "Credit") {
            const updateBalance = hiddenData?.Credit_Balance + (pointValue || 0);
            this.updateCurrentBalanceStore(updateBalance).subscribe({
              error: (err) => console.error("Update failed:", err),
            });
          }

        },
        onReady: () => {
          this.loadingService.dismiss().catch((err) => {
            console.error("Error dismissing loading service:", err);
          });
        },
      })
      ?.open();
  }

  typeForm(surveyLink: string, hiddenData: Data, notificationData: Data): void {
    this.loadingService.present();
    window['typeformEmbed']
      ?.makePopup(surveyLink, {
        hideHeaders: true,
        hideFooter: true,
        hidden: [hiddenData],
        onSubmit: () => {
          const route = notificationData?.notification_id?.path;
          if (!route) {
            console.error("Invalid notification path.");
            return;
          }
          // Reference the notification document
          const doc_id = route?.substring(route.lastIndexOf("/") + 1) || null;
          const path_id = "pGxBYtRO4mdgNkboYdSy";
          const doc_data = { viewed: true };
          const dv = { docId: doc_id };
          const method = "update";

          this.authService.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            dv,
            method,
          }).subscribe({
            next: () => {
              this.issueAdditionalAwardId(
                notificationData?.action?.award_id,
                notificationData?.action?.reason
              );
            },
            error: (error) => {
              console.error("Error updating notification viewed status:", error);
            }
          });
        },
        onReady: () => {
          this.loadingService.dismiss().catch((err) => {
            console.error("Error dismissing loading service:", err);
          });
        },
      })
      ?.open();
  }
  issueAdditionalAwardId(awardId, reason) {
    this.authService
      ?.getV2CloudFunction("apisyncv2", this.requestProfileService?.issueReferralGiftCard(awardId, reason))
      .subscribe();
  }

  // this code is used to upating Credit balance
  updateCurrentBalanceStore(updateBalance: number): Observable<any> {
    return this.authService.getUid$("AuthGamesService").pipe(
      switchMap((userId) => {
        if (!userId) {
          console.error("Invalid User ID.");
          return of(null);
        }

        const path_id = "SK8mBmn1R6bNDdj1cul8";
        const doc_data = { "preferred_currency.credit": updateBalance };
        const method = "update";

        // return the observable instead of subscribing here
        return this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).pipe(
          catchError((err) => {
            console.error("Error updating preferred currency credit:", err);
            return of(null);
          })
        );
      })
    );
  }

  // This function calls the cloud function userHomeTranslation, which creates a translation document for the user's home card.
  translateGameCard() {
    const userId = this.authService.getUid$("AuthGamesService").pipe(
      switchMap((userId) => {
        return of(userId) || of(null);
      }));
    this.angularFireFunctions.httpsCallable("userHomeTranslation")(userId)?.subscribe();
  }


  // This function is called when the user wants to translate a game's info
  // into a flat card. It calls a cloud function that will attempt to
  // translate the game's info into a flat card. The cloud function will
  // return the translated flat card.
  translateGameInfoFlatCard(): Observable<any> {
    return this.authService.getUid$("AuthGamesService").pipe(
      switchMap((userId) => {
        if (!userId) {
          console.error("Invalid User ID.");
          return of(null);
        }

        const data = { userId };

        // Return the observable instead of subscribing inside
        return this.getV2CloudFunction("gameinfoflattranslationv2", data).pipe(
          catchError((err) => {
            console.error("Error in gameinfoflattranslationv2:", err);
            return of(null);
          })
        );
      })
    );
  }

  /**
  * "This function calls a cloud function that translates the game info round card for the user."
  * </code>
  */
  translateGameInfoRoundCard() {
    const userId = this.authService.getUid$("AuthGamesService").pipe(
      switchMap((userId) => {
        return of(userId) || of(null);
      }));
    this.angularFireFunctions.httpsCallable("gameInfoRoundTranslation")(userId)?.subscribe();
  }
  //This function is used to call getactivegameminidocv2 CF
  getGameMiniData() {
    try {
      const functions = getFunctions();
      functions.region = AuthService.FIREBASE_REGION;
      const getGameMiniData = httpsCallable(functions, "getactivegameminidocv2");
      return getGameMiniData();
    } catch (error) {
      throw new Error(error);
    }
  }

  liftRestrictionInboxMessage(gamePrice: string) {
    try {
      const userId = this.authService.getUid$("AuthGamesService").pipe(
        switchMap((userId) => {
          return of(userId) || of(null);
        }));
      const data = {
        user_id: userId,
        game_price: gamePrice,
        call_from: "lift_restriction",
      };
      this.getV2CloudFunction("syndicateofferpromotionv2", data).subscribe();
    } catch (error) {
      throw new Error(error);
    }
  }


  // This method is used to call scratchGameNotification CF with user details after use login
  scratchGameNotificationService(userId) {
    try {
      this.getV2CloudFunction("scratchgamenotificationv2", { userId: userId }).pipe(
        catchError((error) => {
          console.error('Error in scratchGameNotificationService', error);
          return of({ success: false, code: 'Error in scratchGameNotificationService', message: 'Error in scratchGameNotificationService' });
        })
      ).subscribe({
        next: (response) => {
          if (response && response.success) {
          } else {
            return of({ success: false, code: 'Cloud function call failed', message: 'Cloud function call failed' });
          }
        },
        error: (error) => {
          return of({ success: false, code: 'Subscription error in scratchGameNotificationService', message: 'Subscription error in scratchGameNotificationService' });

        },
      });
    } catch (error) {
      return of({ success: false, code: 'Unexpected error in scratchGameNotificationService', message: 'Unexpected error in scratchGameNotificationService' });
    }
  }

  public getUnsubscribeGameFeedInbox() {
    this.gamefeedUnsubscribeInbox$ = new Subject<void>();
    return this.gamefeedUnsubscribeInbox$;
  }

  public unsubscribeGameFeedInbox() {
    this.gamefeedUnsubscribeInbox$?.next();
    this.gamefeedUnsubscribeInbox$?.complete();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.unsubscribe();
    this.unsubscribeGameFeedInbox();
  }
}
